import dayjs from "dayjs";
import { formatFairPayMonthYear } from "@components/utils/fairPayFormat";

/**
 * Coverage-gap pill state resolution (ER-11714, Fair Pay Positions list).
 * Pure helpers so the severity/label logic is unit-testable independently of
 * the presentational CoverageGapBadge.vue.
 *
 * Gap shape: { from: ISO date, to: ISO date | null } (from the shared
 * mapSubscription() mapper — coverage_gaps -> coverageGaps).
 */

export const COVERAGE_GAP_SEVERITY = {
  NONE: "none",
  DANGER: "danger",
  WARNING: "warning",
};

export const COVERAGE_GAP_DANGER_LABEL = "No pay range";

function parseDate(value) {
  if (value == null || value === "") return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

/** A gap is relevant when it is still open-ended or ends after `today`. */
function gapExtendsPastToday(gap, today) {
  if (gap.to == null || gap.to === "") return true;
  const to = parseDate(gap.to);
  return to == null || to.isAfter(today, "day");
}

/**
 * Collapse a subscription's coverage gaps into a single pill spec.
 *
 * - none:    no gaps, or every gap is already wholly in the past.
 * - danger:  at least one gap spans today (from <= today && (to is null || to > today)).
 * - warning: no gap spans today but at least one gap starts after today.
 *            Label uses the earliest such gap's start, "Gap from <MMM YYYY>".
 *
 * The most severe state wins (danger > warning > none).
 */
export function resolveCoverageGap(coverageGaps = [], today) {
  const todayDate = parseDate(today) || dayjs();
  const gaps = (Array.isArray(coverageGaps) ? coverageGaps : []).filter((gap) => {
    const from = parseDate(gap && gap.from);
    if (!from) return false;
    return gapExtendsPastToday(gap, todayDate);
  });

  if (gaps.length === 0) {
    return { severity: COVERAGE_GAP_SEVERITY.NONE, label: null, startDate: null };
  }

  const spansToday = gaps.some((gap) => {
    const from = parseDate(gap.from);
    // from <= today (avoid the isSameOrBefore plugin; dayjs core isBefore/isAfter only).
    return !from.isAfter(todayDate, "day");
  });

  if (spansToday) {
    return {
      severity: COVERAGE_GAP_SEVERITY.DANGER,
      label: COVERAGE_GAP_DANGER_LABEL,
      startDate: null,
    };
  }

  const earliest = gaps.reduce((a, b) =>
    parseDate(a.from).isBefore(parseDate(b.from), "day") ? a : b
  );
  return {
    severity: COVERAGE_GAP_SEVERITY.WARNING,
    label: `Gap from ${formatFairPayMonthYear(earliest.from)}`,
    startDate: earliest.from,
  };
}
